// Section configuration for grouping field mappings
export const fieldSections = {
  customerDetails: {
    title: "Customer Details",
    fields: [
      "End_Customer_Duns_Number__c",
      "End_Customer_Name__c",
      "End_Customer_Industry__c",
      "End_Customer_Website__c",
      "End_Customer_HQ__c",
      "End_Customer_Postal_Code__c",
      "End_Customer_City__c",
      "End_Customer_State__c",
      "End_Customer_Billing_Address__c"
    ],
    expanded: true
  },
  projectDetails: {
    title: "Project Details",
    fields: [
      "Partner_primary_need_from_AWS__c",
      "Primary_Needs_From_Aws__c",
      "Partner_project_title__c",
      "Activity_Type__c",
      "Opportunity_Name__c",
      "Sales_Activities__c",
      "ACE_Stage__c",
      "Customer_Business_Problem__c",
      "Solutions_offered__c",
      "Other_solution_description__c",
      "AWS_products__c",
      "NextStep__c",
      "Customer_Use_Case__c",
      "Project_Delivery_Models__c",
      "Amount__c",
      "CloseDate__c",
      "APN_programs__c"
    ],
    expanded: true
  },
  marketingDetails: {
    title: "Opportunity Marketing Details",
    fields: [
      "Marketing_Source__c",
      "Marketing_Campaign__c",
      "Marketing_Use_Cases__c",
      "Marketing_Channels__c",
      "Aws_Funding_Used__c"
    ],
    expanded: true
  },
  additionalDetails: {
    title: "Additional Details",
    fields: [
      "Competitive_Tracking__c",
      "Other_Competitor_Names__c",
      "End_Customer_AWS_Account_ID__c",
      "Description__c"
    ],
    expanded: true
  },
  customerContact: {
    title: "Customer Contact Details",
    tooltip: "Request you to fill in the end customer details in this section. Though optional, it assists AWS in progressing faster. If empty, AWS will populate with the submitter's info.",
    fields: [
      "Owner_First_Name__c",
      "Owner_Last_Name__c",
      "Business_Title__c",
      "Owner_Email__c",
      "Owner_Phone__c"
    ],
    expanded: true
  },
  partnerPrimaryContact: {
    title: "Partner Primary Contact",
    fields: [
      "Partner_Primary_First_Name__c",
      "Partner_Primary_Last_Name__c",
      "Partner_Primary_Title_Business__c",
      "Partner_Primary_Email__c",
      "Partner_Primary_Phone__c"
    ],
    expanded: true
  },
  partnerContact: {
    title: "Partner Contact Details",
    description: "Provide contact information for the primary sales contact responsible for this opportunity within your organization.",
    fields: [
      "Partner_Sales_Contact_Name__c",
      "Partner_Sales_Contact_Email__c"
    ],
    expanded: true
  }
};

// Group mappings by section based on field configuration
export const groupMappingsBySection = (mappings) => {
  const grouped = {
    customerDetails: [],
    projectDetails: [],
    marketingDetails: [],
    additionalDetails: [],
    customerContact: [],
    partnerPrimaryContact: [],
    partnerContact: [],
    ungrouped: []
  };

  mappings.forEach((mapping) => {
    let assigned = false;

    // Check each section for this field
    Object.keys(fieldSections).forEach((sectionKey) => {
      if (
        fieldSections[sectionKey].fields.includes(
          mapping.targetField.apiName
        )
      ) {
        grouped[sectionKey].push(mapping);
        assigned = true;
      }
    });

    // If field not assigned to any section, add to ungrouped
    if (!assigned) {
      grouped.ungrouped.push(mapping);
    }
  });

  // Sort each section's mappings according to the order defined in fieldSections
  Object.keys(grouped).forEach((sectionKey) => {
    if (sectionKey !== 'ungrouped' && fieldSections[sectionKey]) {
      const fieldsOrder = fieldSections[sectionKey].fields;
      grouped[sectionKey].sort((a, b) => {
        const indexA = fieldsOrder.indexOf(a.targetField.apiName);
        const indexB = fieldsOrder.indexOf(b.targetField.apiName);
        return indexA - indexB;
      });
    }
  });

  return grouped;
};

// Filter mappings based on selected filters
export const getFilteredMappings = (fieldMappings, statusFilter, mappingFilter) => {
  let filtered = [...fieldMappings];

  // Helper function to check if a mapping has a valid source field
  const hasValidMapping = (m) => {
    if (!m.sourceFieldApiName) return false;
    const trimmed = String(m.sourceFieldApiName).trim();
    return trimmed !== "" && trimmed.toLowerCase() !== "none";
  };

  // Filter by mapping status
  if (mappingFilter === "Mapped") {
    filtered = filtered.filter(hasValidMapping);
  } else if (mappingFilter === "Unmapped") {
    filtered = filtered.filter((m) => !hasValidMapping(m));
  }

  // Filter by requirement (using Status filter)
  if (statusFilter === "Required") {
    filtered = filtered.filter((m) => m.targetField.required);
  } else if (statusFilter === "Optional") {
    filtered = filtered.filter((m) => !m.targetField.required);
  }

  return filtered;
};

// Map HubSpot CRM properties response to source field format used in FE
export const mapHubspotPropertiesToSourceFields = (properties = [], objectLabel = "deals") => {
  const mapType = (type, fieldType) => {
    const normalizedType = (type || "").toLowerCase();
    const normalizedFieldType = (fieldType || "").toLowerCase();

    if (normalizedType === "enumeration") {
      if (normalizedFieldType === "checkbox") return "MULTIPICKLIST";
      if (normalizedFieldType === "booleancheckbox") return "BOOLEAN";
      if (normalizedFieldType === "calculation_equation") return "PICKLIST";
      return "PICKLIST";
    }

    if (normalizedType === "string") {
      // HubSpot text fields map to ACE STRING; textarea/longtext map to ACE TEXTAREA
      if (normalizedFieldType === "textarea" || normalizedFieldType === "longtext") return "TEXTAREA";
      if (normalizedFieldType === "text") return "STRING";
      if (normalizedFieldType === "phonenumber" || normalizedFieldType === "phone_number") return "PHONE";
      if (normalizedFieldType === "email") return "EMAIL";
      if (normalizedFieldType === "url") return "URL";
      return "STRING";
    }

    if (normalizedType === "number" || normalizedFieldType === "number" || normalizedFieldType === "calculation_equation" || normalizedFieldType === "calculation_rollup") return "NUMBER";
    if (normalizedType === "datetime") return "DATETIME";
    if (normalizedType === "date") return "DATE";
    if (normalizedType === "bool") return "BOOLEAN";

    return "STRING";
  };

  return properties
    .map((prop) => ({
      apiName: prop.name,
      label: prop.label || prop.name,
      type: mapType(prop.type, prop.fieldType),
      isCustom: !prop.hubspotDefined,
      objectLabel: (objectLabel || "").toLowerCase(),
      options: prop.options || [],
      rawType: prop.type,
      rawFieldType: prop.fieldType,
    }))
    // Sort to keep UI predictable (by label)
    .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
};
